import React, { useMemo } from 'react';
import { debounce } from 'lodash';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import SelectInput from './SelectInput';
import LabelTooltip from './LabelTooltip';
import { Input } from './ui/input';
import { DatePicker } from './DatePicker';
import Autocomplete from './AutoComplete';

const FormField = ({
    fieldKey,
    label,
    hideLabel = false,
    type,
    data,
    value,
    error,
    handleChange,   // called on explicit selection / final value commit
    onChange,       // called on keystroke (autocomplete only) — for triggering search
    className,
    tooltipText,
    required = false,
    disabled = false,
    loading = false,
    onKeyDown,
    options = [],
    debounceDelay = 300,
}) => {

    // Debounced handler for plain text inputs
    const debouncedChange = useMemo(
        () => debounce((key, val) => handleChange(key, val), debounceDelay),
        [handleChange, debounceDelay]
    );

    const renderInput = () => {
        if (type === 'map' || type === 'list') {
            return data?.length > 0 || loading ? (
                <SelectInput
                    key={fieldKey}
                    fieldKey={fieldKey}
                    type={type}
                    list={data}
                    onChange={(value) => {
                        if (fieldKey === 'vendor' || fieldKey === 'channelPartner') {
                            handleChange(fieldKey, value);
                        } else {
                            if (value) handleChange(fieldKey, value);
                        }
                    }}
                    selectedValue={value}
                    placeholder={`Select ${label}`}
                    allowAddToList={fieldKey === 'vendor' || fieldKey === 'channelPartner'}
                    error={error}
                    disabled={disabled}
                    loading={loading}
                />
            ) : (
                <SelectInput
                    key={fieldKey}
                    fieldKey={fieldKey}
                    type="list"
                    list={[]}
                    onChange={() => {}}
                    selectedValue=""
                    placeholder={`Select ${label}`}
                    allowAddToList={false}
                    error={error}
                    disabled={disabled}
                />
            );
        }

        if (type === 'date') {
            return (
                <DatePicker
                    placeholder={`Select ${label}`}
                    date={value}
                    error={error}
                    disabled={disabled}
                    setDate={(e) => handleChange(fieldKey, e)}
                    past={true}
                />
            );
        }

        if (type === 'autocomplete') {
            return (
                <>
                    <Autocomplete
                        options={options}
                        value={value}
                        placeholder={`Enter ${label}`}
                        // onChange fires on every debounced keystroke via Autocomplete's internal debounce.
                        // We pass it straight to the caller's onChange (e.g. to trigger a search dispatch).
                        // Autocomplete already debounces internally so no extra debounce needed here.
                        onChange={(val) => onChange && onChange(val || "")}
                        // onSelect fires when user picks from the dropdown — commits the value immediately
                        onSelect={(val) => handleChange(fieldKey, val)}
                        error={error}
                    />
                    {error && <small className="text-red-500">{error}</small>}
                </>
            );
        }

        // default: text / number / password etc.
        return (
            <>
                <Input
                    defaultValue={value || ''}
                    onChange={(e) => debouncedChange(fieldKey, e.target.value)}
                    onKeyDown={fieldKey === 'pincode' && typeof onKeyDown === 'function' ? (e) => {
                        if (!onKeyDown(e)) {
                            e.preventDefault();
                        }
                    } : undefined}
                    placeholder={`Enter ${label}`}
                    className="w-full"
                    error={error}
                    disabled={disabled}
                />
                {error && (
                    <small className="text-destructive text-xs mt-1">{error}</small>
                )}
            </>
        );
    };

    return (
        <div className={`col ${className}`}>
            <div>
                {!hideLabel && (
                    <label className="font-bold text-gray-500 mb-4">
                        {label} {required && '*'}
                        {tooltipText && (
                            <LabelTooltip
                                Icon={InformationCircleIcon}
                                content={
                                    <span className="ml-auto max-w-[400px]">
                                        {tooltipText}
                                    </span>
                                }
                            />
                        )}
                    </label>
                )}
                {renderInput()}
            </div>
            {error && type !== 'autocomplete' && (
                <small className="text-destructive text-xs mt-1">{error}</small>
            )}
        </div>
    );
};

export default FormField;